"use client"

import { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import {
  Pencil, Save, X, Globe, PencilRuler, Undo2, Trash2, Languages, Copy, Check,
  ChevronDown, ChevronRight,
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Image as ImageIcon,
} from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { usePasswordCheck } from "./hooks/usePasswordCheck"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import MissieDemo from "./components/MissieDemo"
import ArticleBody, { parseMarkdownToSections } from "./components/ArticleBody"
import type { ArticleSection } from "./components/ArticleBody"
import "./css/AboutPage.css"
import "./css/ResourcesPage.css"
import "./css/MissieDemo.css"

type Lang = "nl" | "en"
type LangEntry = { title: string; body: string }

type Article = {
  slug?: string
  id?: string
  stage?: "draft" | "published" | null
  i18n?: Partial<Record<Lang, LangEntry>>
  languages?: Lang[]
  lang?: Lang
  isFallback?: boolean
  title: { nl?: string; en?: string }
  audience: string
  publishedAt: string
  sections: ArticleSection[]
  cta?: { label: { nl?: string; en?: string }; href: string }
  sceneId?: string
  demoConcept?: { conceptId: string; loop?: number }
  solution?: { heading: { nl?: string; en?: string }; body: { nl?: string; en?: string } }
  isDraft?: boolean
}

const API = "/portal/campaign-creatives-api.php"
const LANG_LABEL: Record<Lang, string> = { nl: "Nederlands", en: "English" }

/** First real paragraph of an article, stripped of Markdown, for the meta description. */
function seoExcerptFromSections(sections: ArticleSection[] | undefined, max = 160): string | undefined {
  if (!sections) return undefined
  for (const s of sections) {
    const raw =
      "type" in s && s.type === "paragraph"
        ? s.text
        : "body" in s && Array.isArray(s.body)
          ? s.body.join(" ")
          : ""
    const text = raw
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/[*_`#>]/g, "")
      .replace(/\s+/g, " ")
      .trim()
    if (text.length > 40) return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text
  }
  return undefined
}
const otherLang = (l: Lang): Lang => (l === "nl" ? "en" : "nl")
const emptyEntry = (): LangEntry => ({ title: "", body: "" })

async function apiPost(action: string, body: unknown) {
  const res = await fetch(`${API}?action=${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body || {}),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.success) throw new Error(data.error || "Er ging iets mis")
  return data
}

const ArticlePage = () => {
  const { slug, id } = useParams<{ slug: string; id: string }>()
  const isPreview = Boolean(id)
  const { language } = useLanguage()
  const { isAdmin, isLoading: authLoading } = usePasswordCheck()
  const [appConfig, setAppConfig] = useState<any>(null)
  const [article, setArticle] = useState<Article | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [notAuthorized, setNotAuthorized] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // ── admin edit state ──
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState<Record<Lang, LangEntry>>({ nl: emptyEntry(), en: emptyEntry() })
  const [editLang, setEditLang] = useState<Lang>("nl")
  const [busy, setBusy] = useState(false)
  const [adminMsg, setAdminMsg] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const draftTitle = draft[editLang].title
  const draftBody = draft[editLang].body
  const setDraftTitle = (v: string) => setDraft((d) => ({ ...d, [editLang]: { ...d[editLang], title: v } }))
  const setDraftBody = (v: string) => setDraft((d) => ({ ...d, [editLang]: { ...d[editLang], body: v } }))

  const t = (path: string, defaultText = "") => {
    if (!appConfig) return defaultText
    const keys = path.split(".")
    let value: any = appConfig
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }
    if (value && typeof value === "object" && value[language.code]) return value[language.code]
    return typeof value === "string" ? value : value?.en || defaultText
  }

  useEffect(() => {
    if (authLoading) return
    const load = async () => {
      setIsLoading(true)
      setNotFound(false)
      setNotAuthorized(false)
      try {
        const config = await loadAppConfiguration(language.code, null, false)
        setAppConfig(config)

        const langQ = `lang=${language.code === "en" ? "en" : "nl"}`

        if (isPreview) {
          try {
            const apiRes = await fetch(`/portal/api/article-preview.php?id=${encodeURIComponent(id || "")}&${langQ}`, {
              credentials: "include",
            })
            const contentType = apiRes.headers.get("content-type") || ""
            const apiData = apiRes.ok && contentType.includes("application/json") ? await apiRes.json() : null
            if (apiData?.success && apiData.article) setArticle(apiData.article)
            else if (apiRes.status === 403) setNotAuthorized(true)
            else setNotFound(true)
          } catch (e) {
            console.warn("Kon voorbeeld niet laden:", e)
            setNotFound(true)
          }
          return
        }

        // Admins read through the session-gated endpoint first so drafts
        // (article_stage = 'draft') resolve; everyone else only ever sees
        // published articles.
        if (isAdmin) {
          try {
            const r = await fetch(`/portal/api/article-preview.php?slug=${encodeURIComponent(slug || "")}&${langQ}`, {
              credentials: "include",
            })
            const ct = r.headers.get("content-type") || ""
            const d = r.ok && ct.includes("application/json") ? await r.json() : null
            if (d?.success && d.article) {
              setArticle(d.article)
              return
            }
            if (r.status === 404) {
              setNotFound(true)
              return
            }
            // 403 or anything else: fall through to the public endpoint
          } catch (e) {
            console.warn("Kon admin-artikel niet laden, val terug op publiek:", e)
          }
        }

        try {
          const apiRes = await fetch(`/portal/api/articles-public.php?slug=${encodeURIComponent(slug || "")}&${langQ}`)
          const contentType = apiRes.headers.get("content-type") || ""
          const apiData = apiRes.ok && contentType.includes("application/json") ? await apiRes.json() : null
          if (apiData?.success && apiData.article) setArticle(apiData.article)
          else setNotFound(true)
        } catch (e) {
          console.warn("Kon gepubliceerd artikel niet laden (portal niet bereikbaar):", e)
          setNotFound(true)
        }
      } catch (e) {
        console.error("Kon artikel niet laden:", e)
        setNotFound(true)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [slug, id, isPreview, language.code, isAdmin, authLoading])

  // ── editor helpers ──────────────────────────────────────────────────────
  const startEditing = () => {
    if (!article) return
    const i18n = article.i18n || {}
    setDraft({
      nl: { title: i18n.nl?.title ?? "", body: i18n.nl?.body ?? "" },
      en: { title: i18n.en?.title ?? "", body: i18n.en?.body ?? "" },
    })
    // Open on the language the reader is viewing (or the one that exists).
    const viewLang = (language.code === "en" ? "en" : "nl") as Lang
    setEditLang(article.languages?.includes(viewLang) ? viewLang : article.lang || viewLang)
    setAdminMsg(null)
    setIsEditing(true)
  }

  const applyToSelection = (
    transform: (sel: string) => { text: string; caretStart?: number; caretEnd?: number },
  ) => {
    const el = bodyRef.current
    if (!el) return
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = el.value.slice(0, start)
    const sel = el.value.slice(start, end)
    const after = el.value.slice(end)
    const { text, caretStart, caretEnd } = transform(sel)
    setDraftBody(before + text + after)
    requestAnimationFrame(() => {
      el.focus()
      const cs = start + (caretStart ?? text.length)
      const ce = start + (caretEnd ?? caretStart ?? text.length)
      el.setSelectionRange(cs, ce)
    })
  }

  const wrap = (token: string, placeholder: string) =>
    applyToSelection((sel) => {
      const inner = sel || placeholder
      return { text: `${token}${inner}${token}`, caretStart: token.length, caretEnd: token.length + inner.length }
    })

  const prefixLines = (prefix: string, placeholder: string) =>
    applyToSelection((sel) => {
      const lines = (sel || placeholder).split("\n")
      const text = lines.map((l) => (l.startsWith(prefix) ? l : prefix + l)).join("\n")
      return { text, caretStart: 0, caretEnd: text.length }
    })

  const insertBlock = (block: string, caretStart: number, caretEnd: number) =>
    applyToSelection(() => ({ text: block, caretStart, caretEnd }))

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || !article?.id) return
    setBusy(true)
    setAdminMsg("Afbeelding uploaden...")
    try {
      const fd = new FormData()
      fd.append("file", file)
      fd.append("id", article.id)
      const res = await fetch(`${API}?action=upload_article_image`, {
        method: "POST",
        credentials: "include",
        body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) throw new Error(data.error || "Upload mislukt")
      // Insert on its own line with an empty caption between the brackets.
      insertBlock(`\n\n![](${data.url})\n\n`, 4, 4)
      setAdminMsg("Afbeelding toegevoegd. Typ een onderschrift tussen de blokhaken [ ].")
    } catch (err: any) {
      setAdminMsg(`Upload mislukt: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  // Which languages differ from what's saved on the article.
  const dirtyLangs = (): Lang[] => {
    const orig = article?.i18n || {}
    return (["nl", "en"] as Lang[]).filter((l) => {
      const d = draft[l]
      const o = orig[l] || { title: "", body: "" }
      return d.title.trim() !== (o.title || "").trim() || d.body.trim() !== (o.body || "").trim()
    })
  }

  const saveArticle = async () => {
    if (!article?.id) return
    const dirty = dirtyLangs()
    if (dirty.length === 0) {
      setIsEditing(false)
      return
    }
    const entries: Record<string, { title: string; body: string }> = {}
    for (const l of dirty) entries[l] = { title: draft[l].title.trim(), body: draft[l].body.trim() }
    setBusy(true)
    setAdminMsg(null)
    try {
      await apiPost("save_article", { id: article.id, entries })
      const nextI18n: Article["i18n"] = {}
      for (const l of ["nl", "en"] as Lang[]) {
        if (draft[l].body.trim()) nextI18n![l] = { title: draft[l].title.trim(), body: draft[l].body.trim() }
      }
      const nextLangs = (["nl", "en"] as Lang[]).filter((l) => nextI18n?.[l])
      const viewLang = (language.code === "en" ? "en" : "nl") as Lang
      const showLang: Lang = nextLangs.includes(viewLang) ? viewLang : nextLangs[0] || editLang
      setArticle({
        ...article,
        i18n: nextI18n,
        languages: nextLangs,
        lang: showLang,
        isFallback: showLang !== viewLang,
        title: { ...article.title, nl: nextI18n?.[showLang]?.title || "" },
        sections: parseMarkdownToSections(nextI18n?.[showLang]?.body || ""),
      })
      setIsEditing(false)
    } catch (err: any) {
      setAdminMsg(`Opslaan mislukt: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  const translateInto = async () => {
    if (!article?.id) return
    const from = otherLang(editLang)
    const src = draft[from]
    if (!src.body.trim() || !src.title.trim()) {
      setAdminMsg(`Er is nog geen volledige ${LANG_LABEL[from]}e tekst om uit te vertalen.`)
      return
    }
    if (
      draft[editLang].body.trim() &&
      !window.confirm(
        `De ${LANG_LABEL[editLang]}e tekst wordt vervangen door een nieuwe vertaling van de ${LANG_LABEL[from]}e tekst zoals die nu in de editor staat. Doorgaan?`,
      )
    ) {
      return
    }
    setBusy(true)
    setAdminMsg(`Bezig met vertalen van ${LANG_LABEL[from]} naar ${LANG_LABEL[editLang]}...`)
    try {
      const data = await apiPost("translate_article", {
        id: article.id,
        from,
        to: editLang,
        article_title: src.title.trim(),
        article_body: src.body.trim(),
      })
      setDraft((d) => ({ ...d, [editLang]: { title: data.article_title || "", body: data.article_body || "" } }))
      setAdminMsg("Vertaling ingevuld vanuit de huidige tekst. Lees na en klik Opslaan (bewaart beide talen).")
    } catch (err: any) {
      setAdminMsg(`Vertalen mislukt: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  const copyForTranslation = async () => {
    const src = draft[editLang].body.trim() ? editLang : otherLang(editLang)
    const e = draft[src]
    const text = `# ${e.title}\n\n${e.body}`
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      window.prompt("Kopieer deze tekst:", text)
    }
  }

  const setStage = async (stage: "draft" | "published" | "off") => {
    if (!article?.id) return
    if (stage === "off" && !window.confirm("Dit artikel van de site halen? Het blijft in je creatives-overzicht staan.")) return
    setBusy(true)
    setAdminMsg(null)
    try {
      const data = await apiPost("set_article_stage", { id: article.id, stage })
      if (stage === "off") {
        window.location.href = "/ontdek"
        return
      }
      const newStage = stage === "published" ? "published" : "draft"
      if (data.slug && data.slug !== article.slug) {
        window.location.href = `/ontdek/${data.slug}`
        return
      }
      setArticle({ ...article, stage: newStage })
      setAdminMsg(stage === "published" ? "Gepubliceerd. Iedereen kan dit nu zien." : "Terug naar concept. Alleen jij ziet dit nu.")
    } catch (err: any) {
      setAdminMsg(`Mislukt: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  const seoArticleTitle = article
    ? article.title[language.code as "nl" | "en"] || article.title.nl || article.title.en || ""
    : ""
  const seoArticleDescription = article ? seoExcerptFromSections(article.sections) : undefined
  const seoPath = isPreview ? `/ontdek/preview/${id || ""}` : slug ? `/ontdek/${slug}` : "/ontdek"
  // Per-article share image, generated by /og.php (matches what ontdek-share.php
  // injects for link scrapers). Preview/hub routes fall back to the default card.
  const seoImage = !isPreview && slug ? `https://techtok.me/og.php?slug=${encodeURIComponent(slug)}` : undefined

  useSeo({
    path: seoPath,
    title: seoArticleTitle
      ? `${seoArticleTitle} | Techtok`
      : language.code === "en"
        ? "Article | Techtok"
        : "Artikel | Techtok",
    description: seoArticleDescription,
    image: seoImage,
    type: "article",
    publishedTime: article?.publishedAt || undefined,
    author: "Techtok",
    noindex: isPreview || notFound || (article?.stage ?? "published") === "draft",
    jsonLd:
      article && seoArticleTitle && !isPreview
        ? {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: seoArticleTitle,
            ...(seoArticleDescription ? { description: seoArticleDescription } : {}),
            ...(article.publishedAt ? { datePublished: article.publishedAt } : {}),
            inLanguage: article.lang === "en" ? "en" : "nl-NL",
            image: seoImage || "https://techtok.me/images/og-card.jpg",
            mainEntityOfPage: `https://techtok.me${seoPath}`,
            author: { "@type": "Organization", name: "Techtok", url: "https://techtok.me/" },
            publisher: {
              "@type": "Organization",
              name: "Techtok",
              logo: { "@type": "ImageObject", url: "https://techtok.me/images/techtok-logo.png" },
            },
          }
        : undefined,
  })

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  const showAdminBar = isAdmin && !isPreview && article
  const stage = article?.stage ?? (article?.isDraft ? "draft" : "published")
  const previewSections = isEditing ? parseMarkdownToSections(draftBody) : article?.sections ?? []
  const displayTitle = isEditing
    ? draftTitle
    : article
      ? article.title[language.code as "nl" | "en"] || article.title.nl
      : ""

  return (
    <div className="landing-selector-container">
      <Navigation appConfig={appConfig} currentPage="ontdek" onNavigate={() => {}} t={t} />

      <main className="main-content">
        {notAuthorized ? (
          <section className="resources-hero">
            <div className="resources-hero-inner">
              <h1 className="missies-page-title">{language.code === "nl" ? "Niet ingelogd" : "Not signed in"}</h1>
              <p className="missies-page-subtitle">
                {language.code === "nl"
                  ? "Log in op het portal als admin om een voorbeeld te bekijken."
                  : "Log in to the portal as an admin to view a preview."}
              </p>
            </div>
          </section>
        ) : notFound || !article ? (
          <section className="resources-hero">
            <div className="resources-hero-inner">
              <h1 className="missies-page-title">{language.code === "nl" ? "Artikel niet gevonden" : "Article not found"}</h1>
              <a href="/ontdek" className="how-it-works-link">
                {language.code === "nl" ? "← Terug naar Ontdek" : "← Back to Discover"}
              </a>
            </div>
          </section>
        ) : (
          <>
            {isPreview && (
              <div className="article-draft-banner">
                {language.code === "nl"
                  ? "Voorbeeld, dit is nog niet gepubliceerd."
                  : "Preview, this is not published yet."}
              </div>
            )}

            {showAdminBar && (
              <div className="article-admin-bar">
                <span className={`article-stage-pill article-stage-pill--${stage}`}>
                  {stage === "published" ? <Globe size={13} /> : <PencilRuler size={13} />}
                  {stage === "published" ? "Live" : "Concept"}
                </span>
                {article?.languages && (
                  <span className="article-lang-hint">
                    <Languages size={13} />
                    {(["nl", "en"] as Lang[])
                      .map((l) => `${l.toUpperCase()}${article.languages?.includes(l) ? "" : " —"}`)
                      .join(" · ")}
                  </span>
                )}
                <div className="article-admin-actions">
                  {!isEditing && (
                    <button className="article-admin-btn" onClick={startEditing} disabled={busy}>
                      <Pencil size={14} /> Bewerken
                    </button>
                  )}
                  {isEditing && (
                    <>
                      <button className="article-admin-btn article-admin-btn--primary" onClick={saveArticle} disabled={busy}>
                        <Save size={14} />{" "}
                        {dirtyLangs().length > 1 ? "Opslaan (NL + EN)" : "Opslaan"}
                      </button>
                      <button
                        className="article-admin-btn"
                        onClick={() => {
                          if (dirtyLangs().length === 0 || window.confirm("Niet-opgeslagen wijzigingen weggooien?")) {
                            setIsEditing(false)
                          }
                        }}
                        disabled={busy}
                      >
                        <X size={14} /> Annuleren
                      </button>
                    </>
                  )}
                  {!isEditing && stage !== "published" && (
                    <button className="article-admin-btn article-admin-btn--primary" onClick={() => setStage("published")} disabled={busy}>
                      <Globe size={14} /> Publiceren
                    </button>
                  )}
                  {!isEditing && stage === "published" && (
                    <button className="article-admin-btn" onClick={() => setStage("draft")} disabled={busy}>
                      <Undo2 size={14} /> Terug naar concept
                    </button>
                  )}
                  {!isEditing && (
                    <button className="article-admin-btn article-admin-btn--danger" onClick={() => setStage("off")} disabled={busy}>
                      <Trash2 size={14} /> Van site halen
                    </button>
                  )}
                </div>
                {adminMsg && <span className="article-admin-msg">{adminMsg}</span>}
              </div>
            )}

            {!isEditing && article?.isFallback && (
              <div className="article-lang-notice">
                {language.code === "nl"
                  ? "Dit artikel is nog niet in het Nederlands. Je ziet de Engelse versie."
                  : "This article isn't available in English yet. You're seeing the Dutch version."}
              </div>
            )}

            <section className="article-hero">
              <div className="article-hero-inner">
                {!isPreview && !isEditing && (
                  <a href="/ontdek" className="article-back-link">
                    {language.code === "nl" ? "← Terug naar Ontdek" : "← Back to Discover"}
                  </a>
                )}
                {isEditing ? (
                  <input
                    className="article-title article-title-input"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    placeholder="Titel van het artikel"
                  />
                ) : (
                  <h1 className="article-title">{displayTitle}</h1>
                )}
              </div>
            </section>

            {isEditing ? (
              <div className="article-editor">
                <div className="article-editor-pane">
                  <div className="article-editor-langs">
                    <div className="article-editor-langtabs">
                      {(["nl", "en"] as Lang[]).map((l) => (
                        <button
                          key={l}
                          type="button"
                          className={`article-lang-tab ${editLang === l ? "is-active" : ""}`}
                          onClick={() => setEditLang(l)}
                        >
                          {LANG_LABEL[l]}
                          {draft[l].body.trim() ? <Check size={12} /> : <span className="article-lang-tab-empty">leeg</span>}
                        </button>
                      ))}
                    </div>
                    <div className="article-editor-langtools">
                      <button
                        type="button"
                        className="article-lang-tool"
                        onClick={translateInto}
                        disabled={busy || !draft[otherLang(editLang)].body.trim()}
                        title={`Vertaalt de ${LANG_LABEL[otherLang(editLang)]}e tekst zoals die nu in de editor staat (opgeslagen of niet) naar ${LANG_LABEL[editLang]}`}
                      >
                        <Languages size={13} /> Vertaal huidige {LANG_LABEL[otherLang(editLang)]}e tekst met AI
                      </button>
                      <button type="button" className="article-lang-tool" onClick={copyForTranslation} title="Kopieer de tekst om zelf te (laten) vertalen">
                        {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Gekopieerd" : "Kopieer voor vertaling"}
                      </button>
                    </div>
                  </div>
                  <div className="article-editor-toolbar">
                    <button type="button" title="Vet" onClick={() => wrap("**", "vet")}><Bold size={15} /></button>
                    <button type="button" title="Cursief" onClick={() => wrap("*", "cursief")}><Italic size={15} /></button>
                    <button type="button" title="Kop" onClick={() => prefixLines("## ", "Kop")}><Heading2 size={15} /></button>
                    <button type="button" title="Subkop" onClick={() => prefixLines("### ", "Subkop")}><Heading3 size={15} /></button>
                    <button type="button" title="Opsomming" onClick={() => prefixLines("- ", "punt")}><List size={15} /></button>
                    <button type="button" title="Genummerd" onClick={() => prefixLines("1. ", "punt")}><ListOrdered size={15} /></button>
                    <button type="button" title="Kader / tip" onClick={() => prefixLines("> ", "tip")}><Quote size={15} /></button>
                    <button
                      type="button"
                      title="Link"
                      onClick={() =>
                        applyToSelection((sel) => {
                          const label = sel || "linktekst"
                          return { text: `[${label}](https://)`, caretStart: label.length + 3, caretEnd: label.length + 11 }
                        })
                      }
                    >
                      <Link2 size={15} />
                    </button>
                    <button type="button" title="Afbeelding uploaden" onClick={() => fileRef.current?.click()} disabled={busy}>
                      <ImageIcon size={15} />
                    </button>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={onPickImage} />
                  </div>
                  <textarea
                    ref={bodyRef}
                    className="article-editor-textarea"
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    placeholder={"Schrijf hier je artikel.\n\n## Een kop\n\nEen alinea met **vet** en *cursief*.\n\n- een punt\n- nog een punt\n\n> Een tip in een kader"}
                    spellCheck
                  />
                  <button
                    type="button"
                    className="article-editor-preview-toggle"
                    onClick={() => setShowPreview((v) => !v)}
                  >
                    {showPreview ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {showPreview
                      ? (language.code === "nl" ? "Verberg resultaat" : "Hide result")
                      : (language.code === "nl" ? "Bekijk resultaat" : "Show result")}
                  </button>
                </div>
                {showPreview && (
                  <article className="article-page article-editor-preview">
                    <ArticleBody sections={previewSections} />
                  </article>
                )}
              </div>
            ) : (
              <article className="article-page">
                <ArticleBody sections={previewSections} />
              </article>
            )}

            {!isEditing && (article.sceneId || article.demoConcept) && (
              <section className="article-exit-banner">
                <div className="article-exit-banner-inner">
                  <div className="article-exit-banner-copy">
                    <h2>
                      {article.solution?.heading?.[language.code as "nl" | "en"] ||
                        article.solution?.heading?.nl ||
                        (language.code === "nl" ? "Zo pakt Techtok dit aan" : "This is how Techtok solves it")}
                    </h2>
                    <p>
                      {article.solution?.body?.[language.code as "nl" | "en"] ||
                        article.solution?.body?.nl ||
                        (language.code === "nl"
                          ? "Speel het zelf: korte, speelse missies, direct in de browser."
                          : "Try it yourself: short, playful missions, right in the browser.")}
                    </p>
                    {article.cta && (
                      <a href={article.cta.href} className="article-exit-banner-cta">
                        {article.cta.label[language.code as "nl" | "en"] || article.cta.label.nl}
                      </a>
                    )}
                  </div>
                  <div className="article-exit-banner-demo">
                    {article.sceneId ? (
                      <div className="article-scene-frame">
                        <iframe
                          src={`/portal/scene-embed.php?id=${encodeURIComponent(article.sceneId)}`}
                          title={article.title.nl || article.title.en || "Techtok Missies"}
                          loading="lazy"
                          className="article-scene-iframe"
                        />
                      </div>
                    ) : (
                      article.demoConcept && (
                        <MissieDemo conceptId={article.demoConcept.conceptId} loop={article.demoConcept.loop} />
                      )
                    )}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default ArticlePage
